import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { apiService } from '../../services/api';
import type { Tender } from '../../types';
import {
  ARCHETYPE_LABELS,
  type CollectionArchetype,
  DOMAIN_ARCHETYPES,
} from '../../types/knowledge';
import { type Column, StandardTable } from '../common/UI/StandardTable';
import type { DocumentCategories, LoadingStates, SupportDoc } from './types';
import { formatUploadedDate, getDropzoneBoxStyle, getLoadingKey } from './utils';

interface SupportDocumentsSectionProps {
  tenderId: string;
  tender: Tender;
  onUpdateTender: (field: keyof Tender, value: Tender[keyof Tender]) => Promise<void>;
  user_docs: SupportDoc[];
  loadingStates: LoadingStates;
  isDragging: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onFileUpload: (
    file: File,
    collectionType?: CollectionArchetype,
    saveToGlobal?: boolean,
    destinationFolder?: string
  ) => void;
  uploadValidationWarning?: string | undefined;
  processingFiles: string[];
  onCategoryChange: (docIdentifier: string, newCategory: string) => void;
  onDeleteDoc: (doc: SupportDoc) => void;
  documentCategories: DocumentCategories;
}

const SupportDocumentsSection: React.FC<SupportDocumentsSectionProps> = ({
  tenderId,
  tender,
  onUpdateTender,
  user_docs,
  loadingStates,
  isDragging,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onFileUpload,
  uploadValidationWarning,
  processingFiles,
  onCategoryChange,
  onDeleteDoc,
  documentCategories,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();

  const prioritizedCollections = new Set(
    tender.prioritizedCollections === undefined
      ? (account?.clientSettings?.defaultKnowledgeBaseSearchPriorities as CollectionArchetype[]) ||
          DOMAIN_ARCHETYPES.bidding
      : (tender.prioritizedCollections as CollectionArchetype[])
  );

  const handleSaveDefaults = async () => {
    try {
      await apiService.updateAccountDetails({
        client: {
          clientSettings: {
            ...account?.clientSettings,
            defaultKnowledgeBaseSearchPriorities: Array.from(prioritizedCollections),
          },
        },
      });
      showSnackbar('Knowledge Base search priorities saved as default.', 'success');
      refreshUserDetails(true);
    } catch (_e) {
      showSnackbar('Failed to save defaults.', 'error');
    }
  };

  const handleCollectionToggle = async (archetype: CollectionArchetype) => {
    const newSelected = new Set(prioritizedCollections);
    if (newSelected.has(archetype)) {
      newSelected.delete(archetype);
    } else {
      newSelected.add(archetype);
    }
    await onUpdateTender('prioritizedCollections', Array.from(newSelected));
  };

  const handleFilesSelected = (files: File[]) => {
    for (const file of files) {
      // Direct upload, defaults to proposal archetype, does not save to global library
      onFileUpload(file, 'bidding_proposal', false, '');
    }
  };

  // Intercept the parent's onDrop to handle our direct upload
  const handleDropIntercept = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDragLeave(e); // Clear visual state
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelected(Array.from(e.dataTransfer.files));
    }
  };

  const userDocsColumns: Column<SupportDoc>[] = [
    {
      id: 'name',
      label: 'Name',
      width: '60%',
      render: (doc) => (
        <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Tooltip title={doc.originalFilename}>
            <Typography variant="body2" noWrap sx={{ display: 'block', fontSize: '1rem', pr: 2 }}>
              {doc.originalFilename}
            </Typography>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {formatUploadedDate(doc.uploadedAt)}
          </Typography>
          {(doc.conversionStatus === 'processing' ||
            doc.conversionStatus === 'failed' ||
            doc.conversionStatus === 'missing_file') && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              {doc.conversionStatus === 'processing' && (
                <CircularProgress size={10} sx={{ mr: 1 }} />
              )}
              <Typography
                variant="caption"
                color={
                  doc.conversionStatus === 'failed' || doc.conversionStatus === 'missing_file'
                    ? 'error'
                    : 'text.secondary'
                }
              >
                {doc.conversionStatus === 'missing_file'
                  ? 'Delete and reupload (File Missing)'
                  : doc.conversionStatus === 'failed'
                    ? 'Conversion Failed'
                    : 'Processing...'}
              </Typography>
            </Box>
          )}
        </Box>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      width: '30%',
      render: (doc) => {
        const docIdentifier = doc.originalFilename?.replace(/\.[^/.]+$/, '') || '';
        let initialCategory = 'bidding_proposal';
        if (documentCategories[docIdentifier]) {
          initialCategory = documentCategories[docIdentifier];
        } else if (doc.doc_type) {
          initialCategory = doc.doc_type;
        } else if (doc.originalFilename) {
          const lowerName = doc.originalFilename.toLowerCase();
          if (/case\s?study/i.test(lowerName)) {
            initialCategory = 'bidding_company_profile';
          }
        }
        return (
          <FormControl fullWidth size="small">
            <Select
              value={initialCategory}
              onChange={(e) => onCategoryChange(docIdentifier, e.target.value)}
              sx={{
                fontSize: '0.8rem',
                backgroundColor: isDarkMode ? theme.palette.background.paper : 'white',
              }}
            >
              {DOMAIN_ARCHETYPES.bidding.map((type) => (
                <MenuItem key={type} value={type}>
                  {ARCHETYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      width: '10%',
      render: (doc) => (
        <IconButton
          edge="end"
          aria-label="delete"
          onClick={() => onDeleteDoc(doc)}
          disabled={!!loadingStates[`delete_${doc.originalFilename}`]}
          size="small"
        >
          {loadingStates[`delete_${doc.originalFilename}`] ? (
            <CircularProgress size={16} />
          ) : (
            <DeleteIcon sx={{ fontSize: 28 }} />
          )}
        </IconButton>
      ),
    },
  ];

  const processingFilesColumns: Column<string>[] = [
    {
      id: 'name',
      label: '',
      render: (fileName) => fileName,
    },
    {
      id: 'processing',
      label: '',
      align: 'right',
      render: () => <CircularProgress size={16} />,
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Global Knowledge Search Priorities */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderColor: 'primary.main',
              bgcolor: 'background.default',
              height: '100%',
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Knowledge Base Search Priorities
              </Typography>
              <Tooltip title="Save as default">
                <IconButton size="small" onClick={handleSaveDefaults} color="primary">
                  <SaveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select which document categories the AI should search through in your Knowledge Base
              when writing this tender.
            </Typography>
            <FormGroup sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {DOMAIN_ARCHETYPES.bidding.map((archetype) => (
                <FormControlLabel
                  key={archetype}
                  control={
                    <Checkbox
                      checked={prioritizedCollections.has(archetype)}
                      onChange={() => handleCollectionToggle(archetype)}
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2">{ARCHETYPE_LABELS[archetype]}</Typography>}
                />
              ))}
            </FormGroup>
            {prioritizedCollections.size === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                You have deselected all Knowledge Base collections. The AI will not use any of your
                company's past data for this tender. You must upload specific files below for it to
                have context.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Upload Area */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              height: '100%',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Upload Tender Specific Files (optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload files that you want apply only to this tender (e.g., CVs, specific projects,
              pricing sheets). The AI reads these files every single time it generates text for this
              tender.
            </Typography>
            <Box
              sx={{
                ...getDropzoneBoxStyle(theme, isDragging),
                flexGrow: 1,
                minHeight: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 1,
              }}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={handleDropIntercept}
            >
              <input
                type="file"
                id="adhoc-upload"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files) {
                    handleFilesSelected(Array.from(e.target.files));
                  }
                }}
              />
              <label
                htmlFor="adhoc-upload"
                style={{
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {loadingStates[getLoadingKey(tenderId, 'upload', 'support')] ? (
                  <CircularProgress size={24} sx={{ mb: 1 }} />
                ) : (
                  <UploadFileIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                )}
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1rem', textAlign: 'center' }}
                >
                  {loadingStates[getLoadingKey(tenderId, 'upload', 'support')]
                    ? 'Uploading...'
                    : 'Drag & Drop files here or click to upload'}
                </Typography>
              </label>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tender Specific Files Table */}
      <Box sx={{ mt: 4, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Files uploaded only for this tender
        </Typography>
        <StandardTable
          columns={userDocsColumns}
          data={user_docs}
          keyExtractor={(doc) => doc.id || doc.originalFilename || ''}
          emptyStateMessage="No documents uploaded yet."
          rowSx={(doc) => {
            const docIdentifier = doc.originalFilename?.replace(/\.[^/.]+$/, '') || '';
            let currentCategory = 'bidding_proposal';
            if (documentCategories[docIdentifier]) {
              currentCategory = documentCategories[docIdentifier];
            } else if (doc.doc_type) {
              currentCategory = doc.doc_type;
            } else if (doc.originalFilename) {
              const lowerName = doc.originalFilename.toLowerCase();
              if (/case\s?study/i.test(lowerName)) {
                currentCategory = 'bidding_company_profile';
              }
            }

            let rowColor = 'transparent';
            if (currentCategory === 'cv') {
              rowColor = isDarkMode ? alpha('#2196f3', 0.16) : '#e3f2fd';
            } else {
              rowColor = isDarkMode ? theme.palette.grey[800] : theme.palette.grey[100];
            }
            return { backgroundColor: rowColor };
          }}
        />
      </Box>

      {/* Upload Validation Warning for Support Docs */}
      {uploadValidationWarning && (
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            backgroundColor: 'bidlogiq.taupe',
            color: 'bidlogiq.black',
            '& .MuiAlert-icon': { color: 'bidlogiq.black' },
            '& .MuiAlert-message': { fontSize: '1rem' },
          }}
        >
          {uploadValidationWarning}
        </Alert>
      )}

      {/* Processing Files List for Support Docs */}
      {processingFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
            Processing Uploads:
          </Typography>
          <StandardTable
            columns={processingFilesColumns}
            data={processingFiles}
            keyExtractor={(fileName) => fileName}
            stickyHeader={false}
          />
        </Box>
      )}
    </Box>
  );
};

export default SupportDocumentsSection;
